import React, { useCallback, useEffect, useState } from "react";
import { Cloud, ShieldCheck, Lock, Plug, Save, Loader2, CheckCircle2, XCircle, Server, Trash2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const ENGINES = [
  {
    key: "azure_openai",
    icon: Cloud,
    label: "Azure OpenAI — your own tenant",
    badge: "Tenant-bound",
    blurb:
      "Redacted payloads go only to the Azure OpenAI resource in your own Azure subscription, your region and under your Microsoft agreement. Nothing reaches this vendor's AI account. This is the configuration enterprise IT departments approve.",
  },
  {
    key: "emergent",
    icon: Server,
    label: "Shared AI gateway — external",
    badge: "External AI",
    blurb:
      "Convenient with no setup, suitable for demos, pilots and smaller organisations. Payloads leave your tenant, so IT will treat this as external AI requiring its own assessment.",
  },
];

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="field-label">{label}</span>
    {children}
    {hint && <span className="mt-1 block text-xs text-slate-500">{hint}</span>}
  </label>
);

export default function AdminAIEngine() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ endpoint: "", deployment: "", api_version: "", api_key: "" });
  const [provider, setProvider] = useState("emergent");
  const [internalOnly, setInternalOnly] = useState(false);
  const [busy, setBusy] = useState("");
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(() => {
    api.get("/ai/engine").then(({ data }) => {
      setCfg(data);
      setProvider(data.provider);
      setInternalOnly(data.internal_only);
      setForm({
        endpoint: data.azure.endpoint || "",
        deployment: data.azure.deployment || "",
        api_version: data.azure.api_version || data.default_api_version,
        api_key: "",
      });
    }).catch((e) => toast.error(apiError(e.response?.data?.detail) || "Could not load the AI engine settings"));
  }, []);

  useEffect(() => { load(); }, [load]);

  const payload = () => ({
    provider,
    internal_only: internalOnly,
    azure: provider === "azure_openai"
      ? { endpoint: form.endpoint, deployment: form.deployment, api_version: form.api_version, api_key: form.api_key || null }
      : null,
  });

  const test = async () => {
    setBusy("test");
    setTestResult(null);
    try {
      const { data } = await api.post("/ai/engine/test", payload());
      setTestResult(data);
      data.ok ? toast.success(data.message) : toast.error(data.message);
    } catch (e) {
      const msg = apiError(e.response?.data?.detail) || "Connection test failed";
      setTestResult({ ok: false, message: msg });
      toast.error(msg);
    } finally {
      setBusy("");
    }
  };

  const save = async () => {
    setBusy("save");
    try {
      await api.put("/ai/engine", payload());
      setForm((f) => ({ ...f, api_key: "" }));
      await load();
      toast.success("AI engine saved. Credentials are encrypted and never returned to the browser.");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const removeAzure = async () => {
    if (!window.confirm("Remove the stored Azure OpenAI credentials? This workspace will fall back to the shared external gateway, and the removal is recorded in the AI audit trail.")) return;
    setBusy("remove");
    try {
      await api.delete("/ai/engine/azure");
      setTestResult(null);
      await load();
      toast.success("Azure credentials removed. The stored key has been destroyed.");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  return (
    <Shell
      testId="admin-ai-engine-page"
      title="AI Engine & Data Boundary"
      subtitle="Choose where AI processing happens. Point the platform at your own Azure OpenAI resource to keep every payload inside your tenant, region and data-processing agreement."
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" data-testid="engine-test-btn" disabled={!!busy} onClick={test} className="gap-2">
            {busy === "test" ? <Loader2 size={14} className="animate-spin" /> : <Plug size={14} />} Test Connection
          </Button>
          <Button data-testid="engine-save-btn" disabled={!!busy} onClick={save} className="gap-2" style={{ backgroundColor: "var(--brand-primary)" }}>
            {busy === "save" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Engine
          </Button>
        </div>
      }
    >
      {!cfg ? (
        <p className="py-8 text-center text-sm text-slate-400">Loading engine settings…</p>
      ) : (
        <div className="space-y-6">
          <section className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="engine-choices">
            {ENGINES.map((e) => {
              const active = provider === e.key;
              return (
                <button
                  key={e.key}
                  data-testid={`engine-option-${e.key}`}
                  onClick={() => setProvider(e.key)}
                  className={`panel p-5 text-left transition-shadow hover:shadow-md ${active ? "ring-2 ring-[color:var(--brand-primary)]" : ""}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <e.icon size={16} style={{ color: active ? "var(--brand-primary)" : "#94A3B8" }} />
                      <h3 className="font-display text-sm font-semibold text-slate-900">{e.label}</h3>
                    </div>
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${e.key === "azure_openai" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                      {e.badge}
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-slate-600">{e.blurb}</p>
                  {active && (
                    <span data-testid={`engine-active-${e.key}`} className="mt-3 inline-block rounded bg-slate-900 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
                      Selected
                    </span>
                  )}
                </button>
              );
            })}
          </section>

          {provider === "azure_openai" && (
            <section className="panel p-5" data-testid="azure-form">
              <h3 className="flex items-center gap-2 font-display text-base font-semibold text-slate-800">
                <Cloud size={15} className="text-slate-400" /> Your Azure OpenAI resource
              </h3>
              <p className="mt-1 text-xs text-slate-500">
                From the Azure portal: your resource → Keys and Endpoint, and Azure AI Foundry → Deployments.
                The key is encrypted with AES-256-GCM before storage and is never sent back to this page.
              </p>
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                <Field label="Endpoint" hint="Must be your Azure resource host, ending .openai.azure.com — no /openai path.">
                  <Input data-testid="azure-endpoint-input" placeholder="https://your-resource.openai.azure.com"
                    value={form.endpoint} onChange={(e) => setForm({ ...form, endpoint: e.target.value })} />
                </Field>
                <Field label="Deployment name" hint="Your deployment name, not the model name.">
                  <Input data-testid="azure-deployment-input" placeholder="gpt-4o-videra"
                    value={form.deployment} onChange={(e) => setForm({ ...form, deployment: e.target.value })} />
                </Field>
                <Field label="API version" hint={`Dated Azure API version. Default ${cfg.default_api_version}.`}>
                  <Input data-testid="azure-apiversion-input" placeholder={cfg.default_api_version}
                    value={form.api_version} onChange={(e) => setForm({ ...form, api_version: e.target.value })} />
                </Field>
                <Field label="API key" hint={cfg.azure.key_present ? "A key is already stored. Leave blank to keep it, or paste a new one to replace it." : "Key 1 or Key 2 from Keys and Endpoint."}>
                  <Input data-testid="azure-key-input" type="password" autoComplete="new-password"
                    placeholder={cfg.azure.key_present ? "•••••••• stored" : "Paste your Azure key"}
                    value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} />
                </Field>
              </div>
              {cfg.azure.last_tested_at && (
                <p className="mt-3 text-xs text-slate-500" data-testid="azure-last-test">
                  Last connection test {String(cfg.azure.last_tested_at).slice(0, 19).replace("T", " ")} —{" "}
                  <span className={cfg.azure.last_test_status === "pass" ? "font-semibold text-emerald-700" : "font-semibold text-red-700"}>
                    {cfg.azure.last_test_status === "pass" ? "passed" : "failed"}
                  </span>
                </p>
              )}
              {cfg.azure.key_present && (
                <div className="mt-4 border-t border-slate-100 pt-3">
                  <Button variant="outline" size="sm" data-testid="azure-remove-btn" disabled={!!busy} onClick={removeAzure}
                    className="gap-2 border-red-200 text-red-700 hover:bg-red-50">
                    <Trash2 size={13} /> Remove stored credentials
                  </Button>
                  <span className="ml-2 text-xs text-slate-500">
                    Destroys the encrypted key and reverts this workspace to the shared gateway. Recorded in the audit trail.
                  </span>
                </div>
              )}
            </section>
          )}

          <section className="panel p-5" data-testid="internal-only-lock">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="flex items-center gap-2 font-display text-base font-semibold text-slate-800">
                  <Lock size={15} className="text-slate-400" /> Internal AI only (hard lock)
                </h3>
                <p className="mt-1 max-w-3xl text-xs text-slate-600">
                  When locked, this workspace may only use a tenant-bound engine. If no Azure OpenAI resource is
                  configured and reachable, AI drafting is refused at the boundary rather than silently falling back
                  to an external engine. Every other feature of the platform continues to work normally.
                </p>
              </div>
              <Switch data-testid="internal-only-switch" checked={internalOnly} onCheckedChange={setInternalOnly} />
            </div>
          </section>

          <section className={`panel p-5 ${cfg.tenant_bound ? "border-l-4 border-l-emerald-500" : "border-l-4 border-l-amber-500"}`} data-testid="engine-boundary-summary">
            <h3 className="flex items-center gap-2 font-display text-base font-semibold text-slate-800">
              <ShieldCheck size={15} className={cfg.tenant_bound ? "text-emerald-600" : "text-amber-500"} /> Current saved position
            </h3>
            <p className="mt-2 text-sm text-slate-700" data-testid="engine-boundary-text">
              {cfg.tenant_bound
                ? `Tenant-bound. Redacted payloads are sent only to ${cfg.azure.endpoint}, inside your own Azure tenant and agreement.`
                : "External AI. Redacted payloads leave your tenant for the shared gateway. Configure your own Azure OpenAI resource above to make this tenant-bound."}
            </p>
            {cfg.internal_only && !cfg.tenant_bound && (
              <p className="mt-2 text-sm font-semibold text-red-700" data-testid="engine-lock-warning">
                Internal-AI-only is locked on but no tenant-bound engine is saved, so AI drafting is currently disabled.
              </p>
            )}
          </section>

          {testResult && (
            <section className="panel p-5" data-testid="engine-test-result">
              <div className="flex items-center gap-2">
                {testResult.ok ? <CheckCircle2 size={16} className="text-emerald-600" /> : <XCircle size={16} className="text-red-600" />}
                <p className="text-sm font-semibold text-slate-800">{testResult.message}</p>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                The test uses the same redaction and audit path as a live call, so it appears in the AI Audit Log.
              </p>
            </section>
          )}
        </div>
      )}
    </Shell>
  );
}
